import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faCheck,
  faEnvelope,
  faShieldHalved,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import emailjs from "emailjs-com";
import image2 from "../../images/AxiomosLogo.png";
import "./GetInTouch.css";

const EMAILJS_CONFIG = {
  serviceId:
    process.env.REACT_APP_EMAILJS_SERVICE_ID || "service_ystlmqj",
  templateId:
    process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "template_pwxep1n",
  publicKey:
    process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "efnQ_-lOOJQ0YcFtY",
};

const initialStatus = { type: "idle", message: "" };

function GetInTouch({ id = "career-enquiry" }) {
  const [status, setStatus] = useState(initialStatus);
  const isSending = status.type === "sending";

  const sendEmail = async (event) => {
    event.preventDefault();

    const form = event.currentTarget;
    setStatus({
      type: "sending",
      message: "Sending your enquiry…",
    });

    try {
      await emailjs.sendForm(
        EMAILJS_CONFIG.serviceId,
        EMAILJS_CONFIG.templateId,
        form,
        EMAILJS_CONFIG.publicKey,
      );

      form.reset();
      setStatus({
        type: "success",
        message: "Thank you — your enquiry has been sent successfully. We’ll be in touch soon.",
      });
    } catch (error) {
      console.error("EmailJS career enquiry failed:", error);
      setStatus({
        type: "error",
        message: "We couldn’t send your enquiry. Please try again or contact us on WhatsApp.",
      });
    }
  };

  const clearPreviousStatus = () => {
    if (status.type === "success" || status.type === "error") {
      setStatus(initialStatus);
    }
  };

  return (
    <section
      id={id}
      className="get-in-touch"
      aria-labelledby={`${id}-title`}
    >
      <div className="get-in-touch__decoration" aria-hidden="true" />

      <div className="get-in-touch__container">
        <div className="get-in-touch__form-panel">
          <p className="get-in-touch__eyebrow">
            <span aria-hidden="true" />
            Career enquiries
          </p>

          <h2 id={`${id}-title`} className="get-in-touch__title">
            Let’s explore the right opportunity together.
          </h2>

          <p className="get-in-touch__description">
            Tell us about your experience, the kind of work you are interested
            in, and how you would like to contribute at Axiomos.
          </p>

          <form
            className="get-in-touch__form"
            onSubmit={sendEmail}
            onChange={clearPreviousStatus}
          >
            <div className="get-in-touch__field-grid">
              <div className="get-in-touch__field">
                <label htmlFor="career-name">Full name</label>
                <input
                  type="text"
                  id="career-name"
                  name="name"
                  placeholder="Your full name"
                  autoComplete="name"
                  required
                />
              </div>

              <div className="get-in-touch__field">
                <label htmlFor="career-email">Email address</label>
                <input
                  type="email"
                  id="career-email"
                  name="email"
                  placeholder="you@example.com"
                  autoComplete="email"
                  required
                />
              </div>

              <div className="get-in-touch__field">
                <label htmlFor="career-phone">Phone / WhatsApp</label>
                <input
                  type="tel"
                  id="career-phone"
                  name="phone"
                  placeholder="+91 98765 43210"
                  autoComplete="tel"
                  inputMode="tel"
                  required
                />
              </div>

              <div className="get-in-touch__field">
                <label htmlFor="career-role">Role or area of interest</label>
                <input
                  type="text"
                  id="career-role"
                  name="vacancies"
                  placeholder="e.g. React Developer"
                  required
                />
              </div>
            </div>

            <div className="get-in-touch__field">
              <label htmlFor="career-about">Tell us about yourself</label>
              <textarea
                id="career-about"
                name="about"
                rows="5"
                placeholder="Share your experience, strengths, and what you would like to work on."
                required
              />
            </div>

            <label className="get-in-touch__consent" htmlFor="career-privacy">
              <input type="checkbox" id="career-privacy" required />
              <span>
                I agree to Axiomos’s{" "}
                <Link to="/privacy-policy">Privacy Policy</Link>.
              </span>
            </label>

            <div className="get-in-touch__form-footer">
              <button
                type="submit"
                className="get-in-touch__submit"
                disabled={isSending}
                aria-busy={isSending}
              >
                {isSending ? (
                  <>
                    Sending enquiry
                    <FontAwesomeIcon icon={faSpinner} spin aria-hidden="true" />
                  </>
                ) : (
                  <>
                    Send enquiry
                    <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
                  </>
                )}
              </button>

              <p className="get-in-touch__response-time">
                <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
                Every enquiry is reviewed by our team
              </p>
            </div>

            {status.message && (
              <p
                className={`get-in-touch__status get-in-touch__status--${status.type}`}
                role={status.type === "error" ? "alert" : "status"}
                aria-live="polite"
              >
                {status.message}
              </p>
            )}
          </form>
        </div>

        <aside
          className="get-in-touch__contact-panel"
          aria-label="Alternative contact options"
        >
          <span className="get-in-touch__panel-index" aria-hidden="true">
            AX / Careers
          </span>

          <div className="get-in-touch__brand-card">
            <img
              src={image2}
              className="get-in-touch__logo"
              alt="Axiomos Technologies LLP"
              loading="lazy"
              decoding="async"
            />
          </div>

          <div className="get-in-touch__panel-content">
            <p>Build meaningful technology</p>
            <h3>Work on practical digital products with real industry impact.</h3>

            <ul aria-label="What you can expect at Axiomos">
              <li>
                <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
                Exposure to real business challenges
              </li>
              <li>
                <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
                Meaningful product ownership
              </li>
              <li>
                <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
                Cross-functional learning opportunities
              </li>
            </ul>
          </div>

          <div className="get-in-touch__direct-contact">
            <a
              href="mailto:contact@axiomos.in"
              className="get-in-touch__email-link"
            >
              <FontAwesomeIcon icon={faEnvelope} aria-hidden="true" />
              contact@axiomos.in
            </a>

            <a
              href="https://wa.me/917738899255?text=Hi%2C%20I%27d%20like%20to%20ask%20about%20career%20opportunities%20at%20Axiomos."
              className="get-in-touch__whatsapp"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FontAwesomeIcon icon={faWhatsapp} aria-hidden="true" />
              Continue on WhatsApp
              <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
            </a>
          </div>

          <p className="get-in-touch__privacy-note">
            <FontAwesomeIcon icon={faShieldHalved} aria-hidden="true" />
            Your personal information is handled responsibly.
          </p>
        </aside>
      </div>
    </section>
  );
}

export default GetInTouch;
